import { defineStore } from 'pinia';
import type { PrintableItem, PrintProduct } from '~/pages/print/_types';
import { computed, ref } from 'vue';
import { useOrganizationStore } from '~/store/organization';
import { getCurrencySymbol, convertCurrency } from '@exfluential/currency';

export enum ShippingCountry {
  US = 'US',
  GB = 'GB',
  IE = 'IE',
  FR = 'FR',
  DE = 'DE',
}

type PriceTier = {
  quantity: number;
  pricePerUnit: number;
};

export type CountryConfig = {
  currency: string;
  phoneCode: string;
  stateLabel: string;
  statePlaceholder: string;
  stateRequired: boolean;
};

export const COUNTRY_CONFIGS: Record<ShippingCountry, CountryConfig> = {
  [ShippingCountry.US]: {
    currency: 'USD',
    phoneCode: '+1',
    stateLabel: 'State',
    statePlaceholder: 'Enter state',
    stateRequired: true,
  },
  [ShippingCountry.GB]: {
    currency: 'GBP',
    phoneCode: '+44',
    stateLabel: 'County',
    statePlaceholder: 'Enter county',
    stateRequired: false,
  },
  [ShippingCountry.IE]: {
    currency: 'EUR',
    phoneCode: '+353',
    stateLabel: 'County',
    statePlaceholder: 'Enter county',
    stateRequired: false,
  },
  [ShippingCountry.FR]: {
    currency: 'EUR',
    phoneCode: '+33',
    stateLabel: 'Department',
    statePlaceholder: 'Enter department',
    stateRequired: false,
  },
  [ShippingCountry.DE]: {
    currency: 'EUR',
    phoneCode: '+49',
    stateLabel: 'State',
    statePlaceholder: 'Enter state',
    stateRequired: false,
  },
};

export type SupportedCurrency = 'USD' | 'EUR' | 'GBP';

type CheckoutState = {
  step: number;
  shippingAddress: {
    name: string;
    email: string;
    phone: string;
    businessName: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
  shippingRate: {
    service: string;
    amount: number;
    estimatedDays: string;
    originalAmount?: number;
  } | null;
  taxAmount: number;
  currency: SupportedCurrency;
};

type ExtendedPrintProduct = PrintProduct & {
  priceTiers?: PriceTier[];
  weightOz?: number;
};

type ExtendedPrintableItem = Omit<PrintableItem, 'printProduct'> & {
  printProduct: ExtendedPrintProduct;
};

export const usePrintStore = defineStore('print', () => {
  const selectedItems = ref<Record<string, ExtendedPrintableItem>>({});
  const quantities = ref<Record<string, number>>({});
  const checkout = ref<CheckoutState>({
    step: 1,
    shippingAddress: {
      name: '',
      email: '',
      phone: '',
      businessName: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: '',
      },
    },
    shippingRate: null,
    taxAmount: 0,
    currency: '' as SupportedCurrency,
  });

  const previousCurrency = ref<SupportedCurrency | null>(null);

  const hasItems = computed(() =>
    Object.values(quantities.value).some((qty) => qty > 0),
  );

  const itemCount = computed(() => {
    return Object.values(quantities.value).reduce(
      (count, qty) => count + (qty > 0 ? 1 : 0),
      0,
    );
  });

  const applicablePriceTiers = computed(() => {
    const tiers: Record<string, PriceTier | null> = {};

    for (const [key, item] of Object.entries(selectedItems.value)) {
      const totalQuantity = quantities.value[key] || 0;
      if (!totalQuantity || !item?.printProduct.priceTiers?.length) {
        tiers[key] = null;
        continue;
      }

      const sortedTiers = [...item.printProduct.priceTiers].sort(
        (a, b) => b.quantity - a.quantity,
      );

      tiers[key] =
        sortedTiers.find((tier) => totalQuantity >= tier.quantity) ||
        sortedTiers.at(-1) ||
        null;
    }

    return tiers;
  });

  const orgCurrency = computed(() => {
    const orgStore = useOrganizationStore();
    const currency =
      (orgStore.organization as any)?.printStoreCurrency || 'USD';
    return currency;
  });

  const subtotal = computed(() => {
    const currentCurrency = checkout.value.currency || orgCurrency.value;
    const origCurrency = orgCurrency.value as SupportedCurrency;

    return Object.entries(selectedItems.value).reduce((total, [key, item]) => {
      const quantity = quantities.value[key] || 0;
      if (!quantity) return total;

      if (
        item.printProduct.priceOverride !== null &&
        item.printProduct.priceOverride !== undefined
      ) {
        let finalPrice = item.printProduct.priceOverride;

        if (currentCurrency !== origCurrency) {
          finalPrice = convertCurrency(
            item.printProduct.priceOverride,
            origCurrency,
            currentCurrency,
          );
        }

        return total + finalPrice * quantity;
      }
      const sortedTiers = [...(item.printProduct.priceTiers || [])].sort(
        (a, b) => b.quantity - a.quantity,
      );

      const applicableTier =
        sortedTiers.find((tier) => quantity >= tier.quantity) ||
        sortedTiers.at(-1);

      if (!applicableTier) return total;

      const basePrice = applicableTier.pricePerUnit || 0;
      let finalPrice = basePrice;

      if (currentCurrency !== origCurrency) {
        finalPrice = convertCurrency(basePrice, origCurrency, currentCurrency);
      }

      return total + finalPrice * quantity;
    }, 0);
  });

  const shippingCost = computed((): number => {
    return Object.keys(selectedItems.value).length;
  });

  const total = computed((): number => {
    return subtotal.value + shippingCost.value;
  });

  const orderTotals = computed(() => {
    const subtotalValue = subtotal.value;
    const taxValue = subtotalValue;
    const shippingValue = checkout.value.shippingRate?.amount || 0;
    const totalValue = subtotalValue + taxValue + shippingValue;

    return {
      subtotal: subtotalValue,
      salesTax: taxValue,
      shipping: shippingValue,
      total: totalValue,
    };
  });

  const getCurrencyForCountry = (country: string): SupportedCurrency => {
    return (
      (COUNTRY_CONFIGS[country as ShippingCountry]
        ?.currency as SupportedCurrency) ||
      (orgCurrency.value as SupportedCurrency)
    );
  };

  const currencySymbol = computed(() => {
    const currency = checkout.value.currency || orgCurrency.value;
    const symbol = getCurrencySymbol(currency);
    return symbol;
  });

  const setCheckoutData = (
    items: Record<string, ExtendedPrintableItem>,
    quantities_: Record<string, number>,
  ) => {
    selectedItems.value = items;
    quantities.value = quantities_;
  };

  const clearCheckoutData = () => {
    selectedItems.value = {};
    quantities.value = {};
  };

  const updateQuantity = (key: string, quantity: number) => {
    if (quantity > 0) {
      quantities.value[key] = quantity;
    } else {
      Reflect.deleteProperty(quantities.value, key);
      Reflect.deleteProperty(selectedItems.value, key);
    }
  };

  const addItem = (
    key: string,
    item: ExtendedPrintableItem,
    quantity: number,
  ) => {
    if (quantity > 0) {
      selectedItems.value[key] = item;
      quantities.value[key] = quantity;
    } else {
      Reflect.deleteProperty(selectedItems.value, key);
      Reflect.deleteProperty(quantities.value, key);
    }
  };

  const syncCurrencyWithAddress = () => {
    const country = checkout.value.shippingAddress?.address?.country;
    if (!country) return;

    const newCurrency = getCurrencyForCountry(country);
    if (!newCurrency) return;
    previousCurrency.value = checkout.value.currency || null;

    if (
      previousCurrency.value &&
      previousCurrency.value !== newCurrency &&
      checkout.value.shippingRate
    ) {
      const originalAmount =
        checkout.value.shippingRate.originalAmount ||
        checkout.value.shippingRate.amount;

      const convertedAmount = convertCurrency(
        originalAmount,
        previousCurrency.value,
        newCurrency,
      );

      const finalAmount =
        country === 'US' && convertedAmount > 19.99 ? 19.99 : convertedAmount;

      checkout.value.shippingRate = {
        ...checkout.value.shippingRate,
        originalAmount: convertedAmount,
        amount: finalAmount,
      };
    }

    checkout.value.currency = newCurrency;
  };

  const setCheckoutStep = (step: number) => {
    const currentCurrency = checkout.value.currency;
    checkout.value.step = step;

    if (currentCurrency) {
      checkout.value.currency = currentCurrency;
    }
  };

  const setShippingAddress = (address: CheckoutState['shippingAddress']) => {
    checkout.value.shippingAddress = address;
    syncCurrencyWithAddress();
  };

  const resetCheckout = () => {
    checkout.value = {
      step: 1,
      shippingAddress: {
        name: '',
        email: '',
        phone: '',
        businessName: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US',
        },
      },
      shippingRate: null,
      taxAmount: 0,
      currency: orgCurrency.value as SupportedCurrency,
    };
  };

  const setShippingRate = (
    rate: Omit<NonNullable<CheckoutState['shippingRate']>, 'originalAmount'>,
  ) => {
    const orgStore = useOrganizationStore();
    const isFullyFinanced =
      (orgStore.organization as any)?.fullyFinancedPrint ?? false;

    const isUS = checkout.value.shippingAddress?.address?.country === 'US';
    let cappedAmount = isUS && rate.amount > 19.99 ? 19.99 : rate.amount;

    if (isFullyFinanced) {
      cappedAmount = 0;
    }

    checkout.value.shippingRate = {
      ...rate,
      originalAmount: rate.amount,
      amount: cappedAmount,
    };
  };

  const setTaxAmount = (amount: number) => {
    checkout.value.taxAmount = amount;
  };

  const resetAll = () => {
    checkout.value = {
      step: 1,
      shippingAddress: {
        name: '',
        email: '',
        phone: '',
        businessName: '',
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US',
        },
      },
      shippingRate: null,
      taxAmount: 0,
      currency: orgCurrency.value as SupportedCurrency,
    };
    selectedItems.value = {};
    quantities.value = {};
  };

  const initCurrency = () => {
    checkout.value.currency = orgCurrency.value as SupportedCurrency;
  };

  const setCurrency = (currency: string) => {
    if (!currency) return;
    checkout.value.currency = currency as SupportedCurrency;
  };

  const handleCurrencyChange = (
    fromCurrency: SupportedCurrency,
    toCurrency: SupportedCurrency,
  ) => {
    if (!fromCurrency || !toCurrency || fromCurrency === toCurrency) {
      return;
    }

    if (checkout.value.shippingRate) {
      const originalAmount =
        checkout.value.shippingRate.originalAmount ||
        checkout.value.shippingRate.amount;

      const convertedAmount = convertCurrency(
        originalAmount,
        fromCurrency,
        toCurrency,
      );

      const isUS = checkout.value.shippingAddress?.address?.country === 'US';
      const finalAmount =
        isUS && convertedAmount > 19.99 ? 19.99 : convertedAmount;

      checkout.value.shippingRate = {
        ...checkout.value.shippingRate,
        originalAmount: convertedAmount,
        amount: finalAmount,
      };
    }

    checkout.value.currency = toCurrency;

    previousCurrency.value = fromCurrency;
  };

  const convertPrice = (price: number): number => {
    const currentCurrency = checkout.value.currency || orgCurrency.value;
    const origCurrency = orgCurrency.value as SupportedCurrency;

    if (currentCurrency === origCurrency) {
      return price;
    }

    return convertCurrency(price, origCurrency, currentCurrency);
  };

  return {
    selectedItems,
    quantities,
    checkout,

    hasItems,
    itemCount,
    applicablePriceTiers,
    subtotal,
    shippingCost,
    total,
    orderTotals,
    orgCurrency,
    currencySymbol,
    convertPrice,

    getCurrencyForCountry,

    setCheckoutData,
    clearCheckoutData,
    updateQuantity,
    addItem,
    setCheckoutStep,
    setShippingAddress,
    resetCheckout,
    setShippingRate,
    setTaxAmount,
    resetAll,
    initCurrency,
    setCurrency,
    syncCurrencyWithAddress,
    previousCurrency,
    handleCurrencyChange,
  };
});
